import { useEffect, useRef, useState } from 'react';
import { ListProducerRunsResponse, ProducerRun } from 'src/models/common/ExecutionRun';
import { defaultSelection, ExecutionSelection, SHARED_SELECTION } from './executionSelection';

export type LoadExecutionRuns = (producerId: string, period: string) => Promise<ListProducerRunsResponse>;

/**
 * Lists a producer's runs for a period and reports the selection to open on through `onResolved`, once per
 * (producer, period). A failed list counts as no runs, so the screen falls back to the shared numbers.
 */
export default function useExecutionRuns(
    loadRuns: LoadExecutionRuns,
    producerId: string | undefined,
    period: string | undefined,
    onResolved: (selection: ExecutionSelection) => void
): { runs: ProducerRun[]; loading: boolean } {
    const [runs, setRuns] = useState<ProducerRun[]>([]);
    const [loading, setLoading] = useState(false);
    // Latest callback without re-running the fetch when its identity changes.
    const onResolvedRef = useRef(onResolved);
    onResolvedRef.current = onResolved;
    // The (producer, period) on screen, written during render: a response for one just left is dropped even
    // before the effect cleanup below has run.
    const shownRef = useRef('');
    shownRef.current = `${producerId}|${period}`;

    useEffect(() => {
        setRuns([]);
        setLoading(false);
        if (!producerId || !period) return undefined;
        // Drops a slower earlier response after a period switch or unmount.
        let active = true;
        const requested = `${producerId}|${period}`;
        setLoading(true);
        (async () => {
            let response: ListProducerRunsResponse | undefined;
            try {
                response = await loadRuns(producerId, period);
            } catch (ex) {
                // eslint-disable-next-line no-console
                console.warn('Could not list runs; showing the current numbers', ex);
            }
            if (!active || shownRef.current !== requested) return;
            setRuns(response?.runs ?? []);
            setLoading(false);
            onResolvedRef.current(response ? defaultSelection(response) : SHARED_SELECTION);
        })();
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [producerId, period]);

    return { runs, loading };
}
